import React, { useMemo, useCallback } from 'react';
import { Link, Clickable, Box, Icon, Loading, P, ListGrid, H3 } from '@maui/elements';
import { DescriptionList } from '@maui/patterns-description-list';
import { useQuery, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store } from '@maui-mf/common-auth';
import { UpgradeButton } from '@maui/freemium';
import { openModal } from '@horizon/utilities';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}
function __generator(thisArg, body) {
  var _ = {
    label: 0,
    sent: function () {
      if (t[0] & 1) throw t[1];
      return t[1];
    },
    trys: [],
    ops: []
  },
      f,
      y,
      t,
      g;
  return g = {
    next: verb(0),
    "throw": verb(1),
    "return": verb(2)
  }, typeof Symbol === "function" && (g[Symbol.iterator] = function () {
    return this;
  }), g;

  function verb(n) {
    return function (v) {
      return step([n, v]);
    };
  }

  function step(op) {
    if (f) throw new TypeError("Generator is already executing.");

    while (_) try {
      if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
      if (y = 0, t) op = [op[0] & 2, t.value];

      switch (op[0]) {
        case 0:
        case 1:
          t = op;
          break;

        case 4:
          _.label++;
          return {
            value: op[1],
            done: false
          };

        case 5:
          _.label++;
          y = op[1];
          op = [0];
          continue;

        case 7:
          op = _.ops.pop();

          _.trys.pop();

          continue;

        default:
          if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
            _ = 0;
            continue;
          }

          if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
            _.label = op[1];
            break;
          }

          if (op[0] === 6 && _.label < t[1]) {
            _.label = t[1];
            t = op;
            break;
          }

          if (t && _.label < t[2]) {
            _.label = t[2];

            _.ops.push(op);

            break;
          }

          if (t[2]) _.ops.pop();

          _.trys.pop();

          continue;
      }

      op = body.call(thisArg, _);
    } catch (e) {
      op = [6, e];
      y = 0;
    } finally {
      f = t = 0;
    }

    if (op[0] & 5) throw op[1];
    return {
      value: op[0] ? op[1] : void 0,
      done: true
    };
  }
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}

/**
 * Both of these functions are taken from:
 *
 * https://ghe.eng.fireeye.com/GSI-SDE-FIIP/nautilusjs/blob/e7279e930af550fa0012666ddb652a2ad1213bf2/packages/app/src/store/actors/normalizers.js
 */
var normalizeHierarchicalLocations = function (locations) {
    // For each location item, merges the lowest hierarchical location reference
    // object with a name (meaning it's known) into the base.  If no name is
    // found on the expected objects, filter out the item.
    // @ts-ignore
    return locations.reduce(function (acc, location) {
        var _a, _b, _c;
        if (!location) {
            return acc;
        }
        if ((_a = location.country) === null || _a === void 0 ? void 0 : _a.name) {
            location = __assign(__assign({}, location), location.country);
        }
        else if ((_b = location.sub_region) === null || _b === void 0 ? void 0 : _b.name) {
            location = __assign(__assign({}, location), location.sub_region);
        }
        else if ((_c = location.region) === null || _c === void 0 ? void 0 : _c.name) {
            location = __assign(__assign({}, location), location.region);
        }
        else {
            return acc;
        }
        return __spreadArray(__spreadArray([], acc, true), [location], false);
    }, []);
};
var normalizeActor = function (data) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var industries = Array.isArray(data.industries)
        ? data.industries.map(function (industry) { return industry.name; })
        : data.industries || [];
    var aliases = Array.isArray(data.aliases)
        ? (_a = data.aliases) === null || _a === void 0 ? void 0 : _a.map(function (alias) { return alias.name; })
        : data.aliases || [];
    var malware = Array.isArray(data.malware)
        ? (_b = data.malware) === null || _b === void 0 ? void 0 : _b.map(function (malware) { return malware.name; })
        : data.malware || [];
    var targetLocations = Array.isArray((_c = data.locations) === null || _c === void 0 ? void 0 : _c.target)
        ? (_e = (_d = data.locations) === null || _d === void 0 ? void 0 : _d.target) === null || _e === void 0 ? void 0 : _e.map(function (target) { return target.name; })
        : ((_f = data.locations) === null || _f === void 0 ? void 0 : _f.target) || [];
    return {
        aliases: aliases,
        audience: data.audience,
        malware: malware,
        countryCode: (_k = (_j = (_h = (_g = data.locations) === null || _g === void 0 ? void 0 : _g.source) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.country) === null || _k === void 0 ? void 0 : _k.iso2,
        description: data.description,
        id: data.id,
        motivations: data.motivations || [],
        name: data.name,
        // @ts-ignore
        sourceLocations: (_m = normalizeHierarchicalLocations(((_l = data.locations) === null || _l === void 0 ? void 0 : _l.source) || [])) === null || _m === void 0 ? void 0 : _m.map(function (location) { return location.name; }),
        targetLocations: targetLocations,
        industries: industries,
        lastReference: data.last_activity_time,
    };
};

// @ts-ignore
var ENV = window.__maui_mf_env__ || {
    REACT_APP_API_HOST: 'https://qa-api-advantage.mandiant.com',
};

var getAuthHeaders = function () {
    var _a;
    var token = (_a = store.getValue()) === null || _a === void 0 ? void 0 : _a.token;
    if (!token)
        return {};
    return {
        Authorization: "Bearer ".concat(token),
    };
};
var fetch = function (pathname, init) {
    if (init === void 0) { init = {}; }
    return __awaiter(void 0, void 0, void 0, function () {
        var url, options;
        return __generator(this, function (_a) {
            url = new URL(pathname, ENV.REACT_APP_API_HOST);
            options = __assign(__assign({}, init), { headers: __assign(__assign({}, (init.headers || {})), getAuthHeaders()) });
            return [2 /*return*/, window.fetch(url, options).then(function (res) { return __awaiter(void 0, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                if (!res.ok) {
                                    throw new Error(res.status.toString());
                                }
                                return [4 /*yield*/, res.json()];
                            case 1: return [2 /*return*/, _a.sent()];
                        }
                    });
                }); })];
        });
    });
};

var fetchActorById = function (id) {
    return fetch("/api/v4/actor/".concat(id), {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
    });
};

var fetchCampaignById = function (id) {
    return fetch("/api/v4/campaign/".concat(id), {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
    });
};

var fetchMalwareById = function (id) {
    return fetch("/api/v4/malware/".concat(id), {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
    });
};

var useActor = function (id) {
    var _a = useQuery(['actor', id], function () { return fetchActorById(id); }, {
        enabled: typeof id === 'string',
    }), data = _a.data, rest = __rest(_a, ["data"]);
    var actor = useMemo(function () {
        if (!data) {
            return;
        }
        return normalizeActor(data);
    }, [data]);
    return __assign({ actor: actor }, rest);
};

var normalizeCampaign = function (rawCampaign) {
    var _a, _b, _c, _d, _e, _f, _g;
    var actors = ((_a = rawCampaign.actors) === null || _a === void 0 ? void 0 : _a.map(function (actor) { return actor.name; })) || [];
    var malware = ((_b = rawCampaign.malware) === null || _b === void 0 ? void 0 : _b.map(function (malware) { return malware.name; })) || [];
    var tools = ((_c = rawCampaign.tools) === null || _c === void 0 ? void 0 : _c.map(function (tool) { return tool.name; })) || [];
    var targetIndustries = ((_d = rawCampaign.target_industries) === null || _d === void 0 ? void 0 : _d.map(function (industry) { return industry.name; })) || [];
    var targetLocations = ((_f = (_e = rawCampaign.target_locations) === null || _e === void 0 ? void 0 : _e.countries) === null || _f === void 0 ? void 0 : _f.map(function (country) { return country.name; })) || [];
    var campaigns = (_g = rawCampaign === null || rawCampaign === void 0 ? void 0 : rawCampaign.campaigns) === null || _g === void 0 ? void 0 : _g.map(function (_a) {
        var id = _a.id, name = _a.short_name;
        return ({
            id: id,
            name: name,
        });
    });
    var type = rawCampaign.campaign_type.toLowerCase();
    return {
        actors: actors,
        malware: malware,
        tools: tools,
        targetIndustries: targetIndustries,
        targetLocations: targetLocations,
        campaigns: campaigns,
        type: type,
        aliases: rawCampaign.aliases,
        audience: rawCampaign.audience,
        campaignType: rawCampaign.campaign_type,
        description: rawCampaign.description,
        id: rawCampaign.id,
        isPublishable: rawCampaign.is_publishable,
        name: rawCampaign.name,
        shortName: rawCampaign.short_name,
        lastReference: rawCampaign.last_activity_time,
        vulnerabilities: rawCampaign.vulnerabilities,
    };
};

var normalizeMalware = function (data) {
    var aliases = typeof data.aliases === 'string'
        ? data.aliases
        : (data.aliases || []).map(function (alias) { return alias.name; });
    return {
        actors: data.actors || [],
        aliases: aliases,
        audience: data.audience,
        capabilities: data.capabilities,
        cve: data.cve || [],
        counts: data.counts,
        description: data.description,
        fireeye_detection_names: data.detections,
        id: data.id,
        lastUpdated: data.last_activity_time,
        malware: data.malware || [],
        name: data.name,
        operating_systems: data.operating_systems,
        roles: data.roles || [],
        hasYara: data.has_yara,
        isMalicious: data.is_malicious,
    };
};

var useCampaign = function (id) {
    var _a = useQuery(['campaign', id], function () { return fetchCampaignById(id); }), data = _a.data, rest = __rest(_a, ["data"]);
    var campaign = useMemo(function () {
        if (!data) {
            return;
        }
        return normalizeCampaign(data);
    }, [data]);
    return __assign({ campaign: campaign }, rest);
};

var useMalware = function (id) {
    var _a = useQuery(['malware', id], function () { return fetchMalwareById(id); }), data = _a.data, rest = __rest(_a, ["data"]);
    var malware = useMemo(function () {
        if (!data) {
            return;
        }
        return normalizeMalware(data);
    }, [data]);
    return __assign({ malware: malware }, rest);
};

var ClickableAssociation = function (_a) {
    var href = _a.href, onClick = _a.onClick, renderAsLink = _a.renderAsLink, children = _a.children;
    if (renderAsLink) {
        return (React.createElement(Link, { href: href, target: "_blank" }, children));
    }
    return React.createElement(Clickable, { onClick: onClick }, children);
};

var Details = function (props) { return (React.createElement(Box, __assign({ as: "dl", flex: "1", alignContent: "flex-start", padding: "0", display: "flex", flexDirection: "row", flexWrap: "wrap", margin: "0 -0.25em" }, props))); };
var DetailsGroup = function (props) { return (React.createElement(Box, __assign({ flex: "1 1 50%", padding: "0.25em", marginBottom: "0.75em" }, props))); };
var DetailsLabel = function (props) { return (React.createElement(Box, __assign({ as: "dt", color: "colorTextDeemphasis" }, props))); };
var DetailsData = function (props) { return (React.createElement(Box, __assign({ as: "dd", overflow: "hidden", lineHeight: "1.4", marginLeft: "0", display: "-webkit-box", marginTop: "spaceGapSmall", style: {
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    } }, props))); };
Details.Group = DetailsGroup;
Details.Label = DetailsLabel;
Details.Data = DetailsData;

var Freemium = function (_a) {
    var layout = _a.layout;
    return (React.createElement(Box, { width: "100%", display: layout === 'inline' ? 'inline-flex' : 'flex', alignItems: "center", justifyContent: "center", padding: "spacePaddingXxLarge", marginTop: "spaceGapMedium", marginBottom: "spaceGapMedium", backgroundImage: "url(/icons/pattern.svg)", backgroundSize: "contain", backgroundPosition: "center" },
        React.createElement(UpgradeButton, null, "Upgrade To View")));
};

var REDACTED = 'redacted';
var withFreemium = function (_a) {
    var prop = _a.prop, _b = _a.layout, layout = _b === void 0 ? 'inline' : _b;
    return function (Component) {
        var WithFreemium = function (props) {
            return props[prop] === REDACTED ? React.createElement(Freemium, { layout: layout }) : React.createElement(Component, __assign({}, props));
        };
        WithFreemium.displayName = Component.displayName
            ? "WithFreemium(".concat(Component.displayName, ")")
            : 'WithFreemium';
        return WithFreemium;
    };
};

// Sorry Steve, stealing this from you
// https://ghe.eng.fireeye.com/GSI-SDE-FIIP/nautilusjs/blob/master/packages/component-core/src/FlagIcon/countryCodes.js
var isValidFlagIconCode = function (code) {
    return typeof code === 'string' && VALID_ISO2_CODES.includes(code.toLowerCase());
};
var VALID_ISO2_CODES = [
    'ad',
    'ae',
    'af',
    'ag',
    'ai',
    'al',
    'am',
    'ao',
    'aq',
    'ar',
    'as',
    'at',
    'au',
    'aw',
    'ax',
    'az',
    'ba',
    'bb',
    'bd',
    'be',
    'bf',
    'bg',
    'bh',
    'bi',
    'bj',
    'bl',
    'bm',
    'bn',
    'bo',
    'bq',
    'br',
    'bs',
    'bt',
    'bv',
    'bw',
    'by',
    'bz',
    'ca',
    'cc',
    'cd',
    'cf',
    'cg',
    'ch',
    'ci',
    'ck',
    'cl',
    'cm',
    'cn',
    'co',
    'cr',
    'cu',
    'cv',
    'cw',
    'cx',
    'cy',
    'cz',
    'de',
    'dj',
    'dk',
    'dm',
    'do',
    'dz',
    'ec',
    'ee',
    'eg',
    'eh',
    'er',
    'es-ca',
    'es',
    'et',
    'eu',
    'fi',
    'fj',
    'fk',
    'fm',
    'fo',
    'fr',
    'ga',
    'gb-eng',
    'gb-nir',
    'gb-sct',
    'gb-wls',
    'gb',
    'gd',
    'ge',
    'gf',
    'gg',
    'gh',
    'gi',
    'gl',
    'gm',
    'gn',
    'gp',
    'gq',
    'gr',
    'gs',
    'gt',
    'gu',
    'gw',
    'gy',
    'hk',
    'hm',
    'hn',
    'hr',
    'ht',
    'hu',
    'id',
    'ie',
    'il',
    'im',
    'in',
    'io',
    'iq',
    'ir',
    'is',
    'it',
    'je',
    'jm',
    'jo',
    'jp',
    'ke',
    'kg',
    'kh',
    'ki',
    'km',
    'kn',
    'kp',
    'kr',
    'kw',
    'ky',
    'kz',
    'la',
    'lb',
    'lc',
    'li',
    'lk',
    'lr',
    'ls',
    'lt',
    'lu',
    'lv',
    'ly',
    'ma',
    'mc',
    'md',
    'me',
    'mf',
    'mg',
    'mh',
    'mk',
    'ml',
    'mm',
    'mn',
    'mo',
    'mp',
    'mq',
    'mr',
    'ms',
    'mt',
    'mu',
    'mv',
    'mw',
    'mx',
    'my',
    'mz',
    'na',
    'nc',
    'ne',
    'nf',
    'ng',
    'ni',
    'nl',
    'no',
    'np',
    'nr',
    'nu',
    'nz',
    'om',
    'pa',
    'pe',
    'pf',
    'pg',
    'ph',
    'pk',
    'pl',
    'pm',
    'pn',
    'pr',
    'ps',
    'pt',
    'pw',
    'py',
    'qa',
    're',
    'ro',
    'rs',
    'ru',
    'rw',
    'sa',
    'sb',
    'sc',
    'sd',
    'se',
    'sg',
    'sh',
    'si',
    'sj',
    'sk',
    'sl',
    'sm',
    'sn',
    'so',
    'sr',
    'ss',
    'st',
    'sv',
    'sx',
    'sy',
    'sz',
    'tc',
    'td',
    'tf',
    'tg',
    'th',
    'tj',
    'tk',
    'tl',
    'tm',
    'tn',
    'to',
    'tr',
    'tt',
    'tv',
    'tw',
    'tz',
    'ua',
    'ug',
    'um',
    'un',
    'us',
    'uy',
    'uz',
    'va',
    'vc',
    've',
    'vg',
    'vi',
    'vn',
    'vu',
    'wf',
    'ws',
    'xk',
    'ye',
    'yt',
    'za',
    'zm',
    'zw',
];

var ActorFlag = function (_a) {
    var countryCode = _a.countryCode, _b = _a.width, width = _b === void 0 ? '32px' : _b;
    if (!countryCode || !isValidFlagIconCode(countryCode)) {
        return React.createElement(Icon, { width: width, name: "user-secret", flexShrink: "0" });
    }
    return (React.createElement(Box, { as: "img", width: width, src: "/flags/4x3/".concat(countryCode.toLowerCase(), ".svg"), flexShrink: "0" }));
};

var AssociationsActor = function (_a) {
    var id = _a.id, name = _a.name, _b = _a.renderAsLink, renderAsLink = _b === void 0 ? false : _b;
    var _c = useActor(id), actor = _c.actor, isLoading = _c.isLoading;
    var onClick = useCallback(function () {
        openModal(id, name, 'ActorModal');
    }, [id, name]);
    if (isLoading) {
        return React.createElement(Loading, { size: "small" });
    }
    return (React.createElement(ClickableAssociation, { href: "/actors/".concat(id), onClick: onClick, renderAsLink: renderAsLink },
        React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapMedium", color: "colorActivePrimary", _hover: {
                textDecoration: 'underline',
            } },
            React.createElement(ActorFlag, { countryCode: actor === null || actor === void 0 ? void 0 : actor.countryCode, width: "18px" }),
            name)));
};

var AssociationsCampaign = function (_a) {
    var id = _a.id, _b = _a.renderAsLink, renderAsLink = _b === void 0 ? false : _b;
    var _c = useCampaign(id), campaign = _c.campaign, isLoading = _c.isLoading;
    var onClick = useCallback(function () {
        openModal(campaign.id, campaign.shortName, 'CampaignModal');
    }, [campaign]);
    if (isLoading) {
        return React.createElement(Loading, { size: "small" });
    }
    return (React.createElement(ClickableAssociation, { href: "/campaigns/".concat(id), onClick: onClick, renderAsLink: renderAsLink },
        React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapMedium", color: "colorActivePrimary", _hover: {
                textDecoration: 'underline',
            } },
            React.createElement(Icon, { name: campaign.type === 'global' ? 'tri-bullseye' : 'bullseye' }),
            campaign.shortName)));
};

var AssociationsMalware = function (_a) {
    var id = _a.id, name = _a.name, _b = _a.renderAsLink, renderAsLink = _b === void 0 ? false : _b;
    var onClick = useCallback(function () {
        openModal(id, name, 'MalwareModal');
    }, [id, name]);
    return (React.createElement(ClickableAssociation, { href: "/malware/".concat(id), onClick: onClick, renderAsLink: renderAsLink },
        React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapMedium", color: "colorActivePrimary", _hover: {
                textDecoration: 'underline',
            } },
            React.createElement(Icon, { name: "bug" }),
            name)));
};

var typeTargetMap = {
    'threat-actor': 'actors',
    malware: 'malwares',
};
var Associations = function (_a) {
    var associations = _a.associations, _b = _a.campaigns, campaigns = _b === void 0 ? [] : _b, _c = _a.renderChildrenAsLinks, renderChildrenAsLinks = _c === void 0 ? false : _c;
    var _d = useMemo(function () {
        return associations.reduce(function (acc, currAssociation) {
            var target = typeTargetMap[currAssociation.type];
            if (!target) {
                return acc;
            }
            acc[target].push(currAssociation);
            return acc;
        }, {
            malwares: [],
            actors: [],
        });
    }, [associations]), actors = _d.actors, malwares = _d.malwares;
    return (React.createElement(DescriptionList, { marginTop: "0", marginBottom: "0" },
        actors.length > 0 && (React.createElement(DescriptionList.Section, { marginBottom: "0", paddingBottom: "spaceGapSmall" },
            React.createElement(DescriptionList.Term, null,
                React.createElement(P, { margin: "0" }, "Actor Attribution")),
            React.createElement(DescriptionList.Details, null,
                React.createElement(P, { margin: "0" },
                    React.createElement(ListGrid, { list: actors }, function (actor) { return (React.createElement(ListGrid.Item, null,
                        React.createElement(AssociationsActor, { id: actor.id, name: actor.name, renderAsLink: renderChildrenAsLinks }))); }))))),
        campaigns.length > 0 && (React.createElement(DescriptionList.Section, { marginBottom: "0", paddingBottom: "spaceGapSmall" },
            React.createElement(DescriptionList.Term, null,
                React.createElement(P, { margin: "0" }, "Campaign Associations")),
            React.createElement(DescriptionList.Details, null,
                React.createElement(P, { margin: "0" },
                    React.createElement(ListGrid, { list: campaigns }, function (campaign) { return (React.createElement(ListGrid.Item, null,
                        React.createElement(AssociationsCampaign, { id: campaign.id, renderAsLink: renderChildrenAsLinks }))); }))))),
        malwares.length > 0 && (React.createElement(DescriptionList.Section, { marginBottom: "0", paddingBottom: "spaceGapSmall" },
            React.createElement(DescriptionList.Term, null,
                React.createElement(P, { margin: "0" }, "Malware Associations")),
            React.createElement(DescriptionList.Details, null,
                React.createElement(P, { margin: "0" },
                    React.createElement(ListGrid, { list: malwares }, function (malware) { return (React.createElement(ListGrid.Item, null,
                        React.createElement(AssociationsMalware, { id: malware.id, name: malware.name, renderAsLink: renderChildrenAsLinks }))); })))))));
};

var ActorReference = function (_a) {
    var dateString = _a.dateString;
    var formattedDate = useMemo(function () {
        var date = new Date(dateString);
        var options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString(undefined, options);
    }, [dateString]);
    return (React.createElement(P, { textStyle: "sectionLabel1", color: "colorTextDeemphasis", textTransform: "uppercase" }, formattedDate));
};

var Actor = function (_a) {
    var _b, _c, _d, _e, _f;
    var id = _a.id;
    var _g = useActor(id), actor = _g.actor, isFetching = _g.isFetching;
    var onClick = useCallback(function () {
        openModal(actor.id, actor.name, 'ActorModal');
    }, [actor]);
    if (isFetching) {
        return (React.createElement(Box, { textAlign: "center" },
            React.createElement(Loading, null)));
    }
    if (!actor) {
        return null;
    }
    return (React.createElement(Box, null,
        React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "spaceGapSmall" },
            React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapLarge" },
                React.createElement(ActorFlag, { countryCode: actor.countryCode }),
                React.createElement(Clickable, { onClick: onClick },
                    React.createElement(H3, { fontWeight: "bold" }, actor.name))),
            actor.lastReference && (React.createElement(Box, { flexShrink: "0" },
                React.createElement(ActorReference, { dateString: actor.lastReference })))),
        React.createElement(DescriptionWithFreemium$2, { description: actor.description }),
        React.createElement(Details, null,
            ((_b = actor.malware) === null || _b === void 0 ? void 0 : _b.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Related Malware"),
                React.createElement(RelatedMalwareWithFreemium, { malware: actor.malware }))),
            ((_c = actor.aliases) === null || _c === void 0 ? void 0 : _c.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Reported As"),
                React.createElement(AliasesMalwareWithFreemium, { aliases: actor.aliases }))),
            ((_d = actor.sourceLocations) === null || _d === void 0 ? void 0 : _d.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Source Regions"),
                React.createElement(SourceRegionsWithFreemium, { sourceLocations: actor.sourceLocations }))),
            ((_e = actor.targetLocations) === null || _e === void 0 ? void 0 : _e.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Targeted Regions"),
                React.createElement(TargetedLocationsWithFreemium$1, { targetLocations: actor.targetLocations }))),
            ((_f = actor.industries) === null || _f === void 0 ? void 0 : _f.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Targeted Industries"),
                React.createElement(TargetedIndustriesWithFreemium$1, { industries: actor.industries }))))));
};
var DescriptionWithFreemium$2 = withFreemium({ prop: 'description', layout: 'inline' })(function (_a) {
    var description = _a.description;
    return (React.createElement(P, { textStyle: "bodySmall", marginTop: "0", display: "-webkit-box", style: {
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
        }, overflow: "hidden" }, description ? description : 'This actor is known to Mandiant and is under investigation'));
});
var RelatedMalwareWithFreemium = withFreemium({ prop: 'malware', layout: 'inline' })(function (_a) {
    var malware = _a.malware;
    return (React.createElement(Details.Data, { title: malware.sort().join(', ') }, malware.sort().join(', ')));
});
var AliasesMalwareWithFreemium = withFreemium({ prop: 'aliases', layout: 'inline' })(function (_a) {
    var aliases = _a.aliases;
    return (React.createElement(Details.Data, { title: aliases.sort().join(', ') }, aliases.sort().join(', ')));
});
var SourceRegionsWithFreemium = withFreemium({ prop: 'sourceLocations', layout: 'inline' })(function (_a) {
    var sourceLocations = _a.sourceLocations;
    return (React.createElement(Details.Data, { title: sourceLocations.sort().join(', ') }, sourceLocations.sort().join(', ')));
});
var TargetedLocationsWithFreemium$1 = withFreemium({ prop: 'targetLocations', layout: 'inline' })(function (_a) {
    var targetLocations = _a.targetLocations;
    return (React.createElement(Details.Data, { title: targetLocations.sort().join(', ') }, targetLocations.sort().join(', ')));
});
var TargetedIndustriesWithFreemium$1 = withFreemium({ prop: 'industries', layout: 'inline' })(function (_a) {
    var industries = _a.industries;
    return (React.createElement(Details.Data, { title: industries.sort().join(', ') }, industries.sort().join(', ')));
});

var CampaignReference = function (_a) {
    var dateString = _a.dateString;
    var formattedDate = useMemo(function () {
        var date = new Date(dateString);
        var options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString(undefined, options);
    }, [dateString]);
    return (React.createElement(P, { textStyle: "sectionLabel1", color: "colorTextDeemphasis", textTransform: "uppercase" }, formattedDate));
};

var Campaign = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var id = _a.id;
    var _h = useCampaign(id), campaign = _h.campaign, isLoading = _h.isLoading, isError = _h.isError;
    var onClick = useCallback(function () {
        openModal(campaign.id, campaign.name, "CampaignModal");
    }, [campaign]);
    if (isLoading) {
        return (React.createElement(Box, { textAlign: "center" },
            React.createElement(Loading, null)));
    }
    if (isError || !campaign) {
        return null;
    }
    return (React.createElement(Box, null,
        React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "spaceGapSmall" },
            React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapLarge" },
                React.createElement(Icon, { name: campaign.type === 'global' ? 'tri-bullseye' : 'bullseye', size: "lg", flexShrink: "0" }),
                React.createElement(Box, { display: "flex", flexDirection: "column" },
                    React.createElement(Clickable, { onClick: onClick },
                        React.createElement(H3, { fontWeight: "bold", display: "-webkit-box", style: {
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                            }, overflow: "hidden", marginBottom: "0" }, campaign.name)),
                    React.createElement(P, { textStyle: "sectionLabel1", color: "colorTextDeemphasis", marginTop: "0" }, campaign.shortName))),
            campaign.lastReference && (React.createElement(Box, { flexShrink: "0" },
                React.createElement(CampaignReference, { dateString: campaign.lastReference })))),
        React.createElement(DescriptionWithFreemium$1, { description: campaign.description }),
        React.createElement(Details, null,
            ((_b = campaign.campaigns) === null || _b === void 0 ? void 0 : _b.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Associated Campaigns"),
                React.createElement(CampaignsWithFreemium, { campaigns: campaign.campaigns }))),
            ((_c = campaign.actors) === null || _c === void 0 ? void 0 : _c.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Associated Actors"),
                React.createElement(ActorsWithFreemium, { actors: campaign.actors }))),
            ((_d = campaign.malware) === null || _d === void 0 ? void 0 : _d.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Associated Malware"),
                React.createElement(MalwareWithFreemium, { malware: campaign.malware }))),
            ((_e = campaign.tools) === null || _e === void 0 ? void 0 : _e.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Associated Tools"),
                React.createElement(ToolsWithFreemium, { tools: campaign.tools }))),
            ((_f = campaign.targetLocations) === null || _f === void 0 ? void 0 : _f.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Targeted Regions"),
                React.createElement(TargetedLocationsWithFreemium, { targetLocations: campaign.targetLocations }))),
            ((_g = campaign.targetIndustries) === null || _g === void 0 ? void 0 : _g.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Targeted Industries"),
                React.createElement(TargetedIndustriesWithFreemium, { targetIndustries: campaign.targetIndustries }))))));
};
var DescriptionWithFreemium$1 = withFreemium({ prop: 'description', layout: 'inline' })(function (_a) {
    var description = _a.description;
    return (React.createElement(P, { textStyle: "bodySmall", marginTop: "0", display: "-webkit-box", style: {
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
        }, overflow: "hidden" }, description ? description : 'This campaign is known to Mandiant and is under investigation'));
});
var CampaignsWithFreemium = withFreemium({ prop: 'campaigns', layout: 'inline' })(function (_a) {
    var campaigns = _a.campaigns;
    var onClick = useCallback(function (id, name) {
        openModal(id, name, 'CampaignModal');
    }, [campaigns]);
    return (React.createElement(Box, { display: "flex", gap: "spaceGapSmall", marginTop: "spaceGapSmall" }, campaigns
        .map(function (campaign) { return (React.createElement(Clickable, { onClick: function () { return onClick(campaign.id, campaign.name); }, display: "flex", alignItems: "center" },
        React.createElement(Box, { display: "flex", alignItems: "center", color: "colorActivePrimary", _hover: {
                textDecoration: 'underline',
            }, gap: "spaceGapSmall" },
            React.createElement(Icon, { name: "radar", size: "sm" }),
            campaign.name))); })
        .reduce(function (prev, curr) { return [prev, ', ', curr]; })));
});
var ActorsWithFreemium = withFreemium({ prop: 'actors', layout: 'inline' })(function (_a) {
    var rawActors = _a.actors;
    var actors = useMemo(function () {
        return rawActors.sort().join(', ');
    }, [rawActors]);
    return React.createElement(Details.Data, { title: actors }, actors);
});
var MalwareWithFreemium = withFreemium({ prop: 'malware', layout: 'inline' })(function (_a) {
    var rawMalware = _a.malware;
    var malware = useMemo(function () {
        return rawMalware.sort().join(', ');
    }, [rawMalware]);
    return React.createElement(Details.Data, { title: malware }, malware);
});
var ToolsWithFreemium = withFreemium({ prop: 'tools', layout: 'inline' })(function (_a) {
    var rawTools = _a.tools;
    var tools = useMemo(function () {
        return rawTools.sort().join(', ');
    }, [rawTools]);
    return React.createElement(Details.Data, { title: tools }, tools);
});
var TargetedLocationsWithFreemium = withFreemium({ prop: 'targetLocations', layout: 'inline' })(function (_a) {
    var rawTargetLocations = _a.targetLocations;
    var targetLocations = useMemo(function () {
        return rawTargetLocations.sort().join(', ');
    }, [rawTargetLocations]);
    return React.createElement(Details.Data, { title: targetLocations }, targetLocations);
});
var TargetedIndustriesWithFreemium = withFreemium({ prop: 'targetIndustries', layout: 'inline' })(function (_a) {
    var rawTargetIndustries = _a.targetIndustries;
    var targetIndustries = useMemo(function () {
        return rawTargetIndustries.sort().join(', ');
    }, [rawTargetIndustries]);
    return React.createElement(Details.Data, { title: targetIndustries }, targetIndustries);
});

var MalwareReference = function (_a) {
    var dateString = _a.dateString;
    var formattedDate = useMemo(function () {
        var date = new Date(dateString);
        var options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString(undefined, options);
    }, [dateString]);
    return (React.createElement(P, { textStyle: "sectionLabel1", color: "colorTextDeemphasis", textTransform: "uppercase" }, formattedDate));
};

var Malware = function (_a) {
    var _b, _c;
    var id = _a.id;
    var _d = useMalware(id), malware = _d.malware, isLoading = _d.isLoading, isError = _d.isError;
    var onClick = useCallback(function () {
        openModal(malware.id, malware.name, 'MalwareModal');
    }, [malware]);
    if (isLoading) {
        return (React.createElement(Box, { textAlign: "center" },
            React.createElement(Loading, null)));
    }
    if (isError || !malware) {
        return null;
    }
    return (React.createElement(Box, null,
        React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "spaceGapSmall" },
            React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapLarge" },
                React.createElement(Icon, { name: malware.isMalicious === false ? 'tools' : 'bug', size: "lg", flexShrink: "0" }),
                React.createElement(Clickable, { onClick: onClick },
                    React.createElement(H3, { fontWeight: "bold" }, malware.name))),
            malware.lastReference && (React.createElement(Box, { flexShrink: "0" },
                React.createElement(MalwareReference, { dateString: malware.lastReference })))),
        React.createElement(DescriptionWithFreemium, { description: malware.description }),
        React.createElement(Details, null,
            ((_b = malware.roles) === null || _b === void 0 ? void 0 : _b.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Role"),
                React.createElement(RolesWithFreemium, { roles: malware.roles }))),
            malware.lastReference && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Last Reference"),
                React.createElement(LastReferenceWithFreemium, { lastReference: "REDACTED" }))),
            ((_c = malware.aliases) === null || _c === void 0 ? void 0 : _c.length) > 0 && (React.createElement(Details.Group, null,
                React.createElement(Details.Label, null, "Reported As"),
                React.createElement(AliasesWithFreemium, { aliases: malware.aliases }))))));
};
var DescriptionWithFreemium = withFreemium({ prop: 'description', layout: 'inline' })(function (_a) {
    var description = _a.description;
    return (React.createElement(P, { textStyle: "bodySmall", marginTop: "0", display: "-webkit-box", style: {
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
        }, overflow: "hidden" }, description ? description : 'This malware is known to Mandiant and is under investigation'));
});
var RolesWithFreemium = withFreemium({ prop: 'roles', layout: 'inline' })(function (_a) {
    var roles = _a.roles;
    return (React.createElement(Details.Data, { title: roles.sort().join(', ') }, roles.sort().join(', ')));
});
var LastReferenceWithFreemium = withFreemium({ prop: 'lastReference', layout: 'inline' })(function (_a) {
    var lastReference = _a.lastReference;
    return React.createElement(Details.Data, null, lastReference);
});
var AliasesWithFreemium = withFreemium({ prop: 'aliases', layout: 'inline' })(function (_a) {
    var aliases = _a.aliases;
    return (React.createElement(Details.Data, { title: aliases.sort().join(', ') }, aliases.sort().join(', ')));
});

// create a client
var queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: function (failureCount, error) {
                if (failureCount === 3) {
                    return false;
                }
                var errorCode = parseInt(error.message);
                if (errorCode >= 500 && errorCode <= 599) {
                    return true;
                }
                return false;
            },
            refetchOnWindowFocus: false,
        },
    },
});
var AssociationsDetails = function (props) { return (React.createElement(QueryClientProvider, { client: queryClient },
    React.createElement(Associations, __assign({}, props)))); };
var ActorDetails = function (props) { return (React.createElement(QueryClientProvider, { client: queryClient },
    React.createElement(Actor, __assign({}, props)))); };
var CampaignDetails = function (props) { return (React.createElement(QueryClientProvider, { client: queryClient },
    React.createElement(Campaign, __assign({}, props)))); };
var MalwareDetails = function (props) { return (React.createElement(QueryClientProvider, { client: queryClient },
    React.createElement(Malware, __assign({}, props)))); };

export { ActorDetails, AssociationsDetails, CampaignDetails, MalwareDetails };
